import { InactivityModal } from "@/components/inactivityModal";
import LoadingScreen from "@/components/loading-screen";
import { useGetAppConfig } from "@/queries/config/config.api";
import { useGetMe } from "@/queries/me/me.api";
import { getEnv } from "@/utils/runtime-config";
import Keycloak from "keycloak-js";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as Sentry from "@sentry/react";
import { getKeycloakInstance } from "./keycloak";
import {
  markKeycloakInitSettled,
  markKeycloakInitStarted,
} from "./keycloak-ready";
import { AuthContext } from "./index";

const INACTIVITY_MINUTES = Number(getEnv("VITE_INACTIVITY_MINUTES") || 14);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const initialized = useRef<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [keycloak] = useState<Keycloak>(getKeycloakInstance());

  // Hook para obtener configuración de la app
  const {
    data: appConfig,
    isLoading: configLoading,
    isError: configError,
  } = useGetAppConfig();

  // Perfil del usuario: origen único de los feature flags para toda la app.
  // Se consulta sólo tras autenticarse para no dispararlo sin token.
  const {
    data: profile,
    isLoading: profileLoading,
    refetch,
  } = useGetMe({
    enabled: isAuthenticated,
  });

  const permissions = useMemo(() => {
    if (profile?.data?.permissions) {
      return profile.data.permissions;
    }
    return [];
  }, [profile]);

  const authUser = useMemo(() => {
    return {
      id: profile?.data?.id || "",
      name: profile?.data?.name || "",
      family_name: profile?.data?.last_name || "",
      given_name: profile?.data?.name || "",
      email: profile?.data?.email || "",
    };
  }, [profile]);

  const featureFlags = useMemo(
    () => profile?.data?.feature_flags ?? {},
    [profile?.data?.feature_flags],
  );

  const hasPermission = useCallback(
    (permission: string): boolean => {
      return permissions.includes(permission);
    },
    [permissions],
  );

  useEffect(() => {
    if (isAuthenticated && profile?.data) {
      const sendPii = getEnv("SENTRY_SEND_DEFAULT_PII") === "true";
      Sentry.setUser({
        id: profile.data.id || "",
        ...(sendPii
          ? {
              email: profile.data.email || "",
              username: `${profile.data.name} ${profile.data.last_name}`.trim(),
            }
          : {}),
      });
    }
  }, [profile, isAuthenticated]);

  // El token no se persiste en ningún sitio: vive en la instancia de Keycloak
  // y el interceptor de axios lo lee de ahí. Tras un refresh sólo hace falta
  // revalidar el perfil, que es de donde salen permisos y feature flags.
  const updateFromToken = useCallback(() => {
    refetch();
  }, [refetch]);

  const [showModal, setshowModal] = useState(false);

  // Store the initial URL so we can redirect back after authentication
  useEffect(() => {
    if (sessionStorage.getItem("initialUrl")) return;

    const { href, hash } = globalThis.window.location;
    // Este efecto corre antes de que keycloak procese y limpie el fragment, así
    // que la URL puede ser todavía el callback del IdP (#state=...&code=...).
    // Guardarla tal cual haría que el siguiente login use un redirectUri con un
    // fragment previo: el nuevo callback se concatena, keycloak no lo puede
    // parsear, vuelve a pedir login y el ciclo se sostiene solo.
    const isKeycloakCallback =
      hash.includes("code=") || hash.includes("state=");

    sessionStorage.setItem(
      "initialUrl",
      isKeycloakCallback ? href.split("#")[0] : href,
    );
  }, []);

  // Clear the stored URL after auth succeeds (user is back from Keycloak)
  useEffect(() => {
    if (isAuthenticated && initialized.current) {
      sessionStorage.removeItem("initialUrl");
    }
  }, [isAuthenticated]);

  useEffect(() => {
    let temporizador: ReturnType<typeof setTimeout>;
    let contadorIntervalo: ReturnType<typeof setInterval> | null = null;
    let ultimaActualizacionLigera = 0;

    const INACTIVITY_SECONDS = 60 * INACTIVITY_MINUTES;
    const INACTIVITY_MS = INACTIVITY_SECONDS * 1000;
    const THROTTLE_INTERVAL = 500;

    const reiniciarTemporizador = (evento: Event) => {
      const ahora = Date.now();
      const tipoEvento = evento.type;

      const esEventoLigero = ["mousemove", "scroll", "wheel"].includes(
        tipoEvento,
      );
      if (
        esEventoLigero &&
        ahora - ultimaActualizacionLigera < THROTTLE_INTERVAL
      ) {
        return;
      }
      if (esEventoLigero) {
        ultimaActualizacionLigera = ahora;
      }

      localStorage.setItem("activity", ahora.toString());

      clearTimeout(temporizador);
      if (contadorIntervalo) {
        clearInterval(contadorIntervalo);
        contadorIntervalo = null;
      }

      let segundosRestantes = INACTIVITY_SECONDS;

      contadorIntervalo = setInterval(() => {
        segundosRestantes -= 1;
        if (segundosRestantes <= 0 && contadorIntervalo) {
          clearInterval(contadorIntervalo);
          contadorIntervalo = null;
        }
      }, 1000);

      temporizador = setTimeout(() => {
        localStorage.setItem("isOpen", ahora.toString());
        globalThis.window.dispatchEvent(
          new CustomEvent("isOpen-local-change", { detail: ahora.toString() }),
        );
        setshowModal(true);
      }, INACTIVITY_MS);
    };

    const eventos: (keyof DocumentEventMap)[] = [
      "mousemove",
      "keydown",
      "mousedown",
      "touchstart",
      "scroll",
      "wheel",
    ];
    eventos.forEach((ev) =>
      globalThis.window.addEventListener(ev, reiniciarTemporizador),
    );

    reiniciarTemporizador(new Event("init"));

    return () => {
      clearTimeout(temporizador);
      if (contadorIntervalo) clearInterval(contadorIntervalo);
      eventos.forEach((ev) =>
        globalThis.window.removeEventListener(ev, reiniciarTemporizador),
      );
    };
  }, []);

  useEffect(() => {
    if (initialized.current) return;
    // Se cierra la puerta ya, de forma síncrona: entre este punto y el final
    // de `init()` hay un retardo de 1s, y cualquier petición lanzada mientras
    // tanto debe esperar en lugar de salir sin token.
    markKeycloakInitStarted();
    // 1 second delay to ensure Keycloak is initialized before checking auth
    const timer = setTimeout(async () => {
      try {
        const authenticated = await keycloak.init({
          onLoad: "check-sso",
          silentCheckSsoRedirectUri: `${globalThis.window.location.origin}/silent-check-sso.html`,
          checkLoginIframe: false, // Disable iframe checks for simplicity
          pkceMethod: "S256", // Use PKCE for security
          redirectUri:
            sessionStorage.getItem("initialUrl") ||
            globalThis.window.location.href,
        });
        initialized.current = true;
        setIsAuthenticated(authenticated);
        if (authenticated && keycloak.tokenParsed) {
          updateFromToken();
          keycloak.onAuthRefreshSuccess = () => {
            updateFromToken();
          };
          keycloak.onAuthRefreshError = () => {
            console.error("Keycloak token refresh failed");
            keycloak.login({
              redirectUri:
                sessionStorage.getItem("initialUrl") ||
                globalThis.window.location.href,
            });
          };
          keycloak.onAuthLogout = () => {
            setIsAuthenticated(false);
          };
        } else {
          keycloak.login({
            redirectUri:
              sessionStorage.getItem("initialUrl") ||
              globalThis.window.location.href,
          });
        }
      } catch (e) {
        console.error("Error initializing Keycloak:", e);
        initialized.current = true;
      } finally {
        setIsLoading(false);
        // Se abre la puerta pase lo que pase: si `init()` falla y no la
        // abriéramos, toda petición posterior quedaría esperando para siempre.
        markKeycloakInitSettled();
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [keycloak, updateFromToken]);

  useEffect(() => {
    const timer = setInterval(async () => {
      if (initialized.current && keycloak.didInitialize) {
        try {
          await keycloak.updateToken(-1);
          updateFromToken();
        } catch (error) {
          console.error("Error updating Keycloak token:", error);
        }
      }
    }, 1000 * 30); // 1 minute delay to ensure Keycloak is initialized before checking auth

    return () => clearInterval(timer);
  }, [initialized, keycloak, updateFromToken]);

  // Mostrar loading hasta que tanto la autenticación como la configuración estén listas
  const configLoaded = !configLoading && !configError;
  const shouldShowLoading =
    isLoading ||
    authUser === null ||
    (isAuthenticated && !configLoaded) ||
    profileLoading;

  const getUserRoleType = useCallback(() => {
    if (!authUser || !permissions) return "empty";
    let type: "admin" | "editor" | "reader" | "empty" = "empty";
    if (permissions.length === 0) {
      return type;
    }
    if (permissions.some((permission) => permission.includes("read"))) {
      type = "reader";
    }
    if (
      permissions.some((permission) => {
        return (
          permission.includes("update") ||
          permission.includes("edit") ||
          permission.includes("create")
        );
      })
    ) {
      type = "editor";
    }
    if (
      permissions.some((permission) => {
        return (
          permission.includes("roles:create") ||
          permission.includes("roles:update") ||
          permission.includes("roles:delete") ||
          permission.includes("roles:assign-permissions") ||
          permission.includes("users:delete") ||
          permission.includes("users:assign-role") ||
          permission.includes("users:activate") ||
          permission.includes("users:create") ||
          permission.includes("users:update") ||
          permission.includes("users:reset-password")
        );
      })
    ) {
      type = "admin";
    }
    if (type === "empty") {
      type = "editor"; // Default to editor if no permissions match
    }
    return type;
  }, [authUser, permissions]);

  const contextValue = useMemo(
    () => ({
      keycloak,
      authenticated: isAuthenticated,
      initialized: initialized.current,
      authUser,
      permissions,
      hasPermission,
      appConfig: appConfig || null,
      configLoaded,
      getUserRoleType,
      featureFlags,
    }),
    [
      keycloak,
      isAuthenticated,
      authUser,
      permissions,
      hasPermission,
      appConfig,
      configLoaded,
      getUserRoleType,
      featureFlags,
    ],
  );

  if (shouldShowLoading) {
    return <LoadingScreen />;
  }

  return (
    <AuthContext.Provider value={contextValue}>
      <InactivityModal
        onClose={() => {
          setshowModal(false);
        }}
        isOpen={showModal}
      />
      {children}
    </AuthContext.Provider>
  );
};
